import type { TradingStrategyDto } from "../../api/hooks/useTradingStrategies";

type SideFields = Pick<TradingStrategyDto, "publicName" | "chartedSymbol" | "timeframe" | "direction">;

/** A strategy's sides are separate strategies that share a name, a charted symbol and a timeframe. */
export function sideGroupKey(strategy: SideFields): string {
  return `${strategy.publicName}|${strategy.chartedSymbol ?? ""}|${strategy.timeframe}`;
}

// Long & short leads; a direction this build does not know goes last.
const SIDE_ORDER: Record<number, number> = { 3: 0, 1: 1, 2: 2 };

function sideRank(strategy: SideFields): number {
  return SIDE_ORDER[strategy.direction ?? 0] ?? 3;
}

export function bySide(a: SideFields, b: SideFields): number {
  return sideRank(a) - sideRank(b);
}

/**
 * One entry per strategy, holding its sides in side order. Groups keep the order their first side
 * had in the input, so a sorted list stays sorted.
 */
export function groupSides<T extends SideFields>(strategies: T[]): T[][] {
  const groups = new Map<string, T[]>();
  for (const strategy of strategies) {
    const key = sideGroupKey(strategy);
    const group = groups.get(key);
    if (group) {
      group.push(strategy);
    } else {
      groups.set(key, [strategy]);
    }
  }

  const result = [...groups.values()];
  for (const group of result) {
    group.sort(bySide);
  }
  return result;
}

/** How many strategies a list of sides comes to. */
export function countStrategies(strategies: SideFields[]): number {
  const keys = new Set(strategies.map(sideGroupKey));
  return keys.size;
}

/** How many strategies a list of ids comes to. An id the catalogue doesn't hold counts as one. */
export function countStrategiesByIds<T extends SideFields & { id: string }>(
  strategyIds: string[],
  catalogue: T[],
): number {
  const byId = new Map(catalogue.map((strategy) => [strategy.id, strategy]));
  const keys = new Set<string>();
  for (const strategyId of strategyIds) {
    const strategy = byId.get(strategyId);
    const key = strategy ? sideGroupKey(strategy) : strategyId;
    keys.add(key);
  }
  return keys.size;
}

/**
 * Every side of the strategy, itself included, from the catalogue list. A side that has left the
 * catalogue (archived, but still held) is not in the list, so it is added back.
 */
export function sidesOf<T extends SideFields & { id: string }>(strategy: T, catalogue: T[]): T[] {
  const key = sideGroupKey(strategy);
  const sides = catalogue.filter((candidate) => sideGroupKey(candidate) === key);
  const listed = sides.some((side) => side.id === strategy.id);
  if (!listed) {
    sides.push(strategy);
  }
  return sides.sort(bySide);
}
